import React, { useState, useEffect } from 'react'
import QRCode from 'react-qr-code'
import { CalendarIcon, MapPinIcon, TagIcon, TicketIcon } from '@heroicons/react/24/outline'

const TicketTemplate = ({ ticket, user, onPrint }) => {
  const [qrData, setQrData] = useState('')
  const [isReady, setIsReady] = useState(false)

  const getTicketType = (ticket) => {
    if (!ticket) return null
    return ticket.eventId ? 'EVENT' : 'OFFER'
  }

  const getTicketPrice = (ticket) => {
    if (!ticket) return 0
    if (ticket.ticketTypeId?.price) {
      return ticket.ticketTypeId.price
    }
    if (ticket.offerId?.discountedPrice) {
      return ticket.offerId.discountedPrice
    }
    return 0
  }

  const ticketType = getTicketType(ticket)
  const isEvent = ticketType === 'EVENT'
  const ticketData = isEvent ? ticket?.eventId : ticket?.offerId

  // Generate QR code data when ticket is ready
  useEffect(() => {
    if (ticket && ticket.code) {
      try {
        // Use ticket code directly as QR value, or create a simple string identifier
        const data = `TICKET:${ticket.code}:${ticket._id || ''}:${ticketType || 'UNKNOWN'}`
        setQrData(data)
        setIsReady(true)
      } catch (error) {
        console.error('Error generating QR code data:', error)
        setIsReady(false)
      }
    } else {
      setQrData('')
      setIsReady(false)
    }
  }, [ticket, ticketType])

  // Early return if ticket is not provided (after hooks)
  if (!ticket) {
    return null
  }

  return (
    <>
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 8mm;
          }
          body * {
            visibility: hidden;
          }
          #ticket-template, #ticket-template * {
            visibility: visible;
          }
          #ticket-template {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100%;
            margin: 0;
            padding: 0;
            transform: scale(0.88);
            transform-origin: top left;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
      <div className="max-w-2xl mx-auto bg-white p-8 print:p-0 print:max-w-full">
      {/* Printable Ticket Template */}
      <div id="ticket-template" className="border-4 border-dashed border-slate-300 rounded-2xl overflow-hidden bg-white print:border-2 print:rounded-lg print:scale-90 print:origin-top">
        {/* Ticket Header - Gradient */}
        <div className={`p-8 text-white print:p-4 ${
          isEvent 
            ? 'bg-gradient-to-br from-purple-600 to-purple-800' 
            : 'bg-gradient-to-br from-cyan-600 to-cyan-800'
        }`}>
          <div className="flex items-start justify-between mb-6 print:mb-3">
            <div className="flex items-center gap-3 print:gap-2">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm print:w-12 print:h-12">
                {isEvent ? (
                  <CalendarIcon className="w-10 h-10 text-white print:w-8 print:h-8" />
                ) : (
                  <TagIcon className="w-10 h-10 text-white print:w-8 print:h-8" />
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-1 print:text-2xl print:mb-0">{ticketData?.title || 'Ticket'}</h1>
                <p className="text-white/80 text-sm print:text-xs">{ticketType} TICKET</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/30 print:px-2 print:py-1">
                <p className="text-xs text-white/80 mb-1 print:text-[10px] print:mb-0">Ticket Code</p>
                <p className="text-lg font-mono font-bold print:text-sm">{ticket.code}</p>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="mt-6 pt-6 border-t border-white/20 print:mt-3 print:pt-3">
            <p className="text-white/80 text-sm mb-1 print:text-xs print:mb-0">Total Amount</p>
            <p className="text-4xl font-bold print:text-2xl">₦{getTicketPrice(ticket).toLocaleString()}</p>
          </div>
        </div>

        {/* Ticket Body */}
        <div className="p-8 bg-slate-50 print:p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:gap-4">
            {/* Left Column - Details */}
            <div className="space-y-6 print:space-y-3">
              {/* Event/Offer Details */}
              {isEvent && ticket.eventId && (
                <>
                  {ticket.eventId.startsAt && (
                    <div>
                      <div className="flex items-center gap-2 text-slate-600 mb-2">
                        <CalendarIcon className="w-5 h-5" />
                        <span className="text-sm font-semibold">Date & Time</span>
                      </div>
                      <p className="text-slate-900 font-medium">
                        {new Date(ticket.eventId.startsAt).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-slate-600 text-sm">
                        {new Date(ticket.eventId.startsAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}

                  {ticket.eventId.venue && (
                    <div>
                      <div className="flex items-center gap-2 text-slate-600 mb-2">
                        <MapPinIcon className="w-5 h-5" />
                        <span className="text-sm font-semibold">Venue</span>
                      </div>
                      <p className="text-slate-900 font-medium">{ticket.eventId.venue}</p>
                    </div>
                  )}
                </>
              )}

              {!isEvent && ticket.offerId && (
                <>
                  {ticket.offerId.validFrom && ticket.offerId.validUntil && (
                    <div>
                      <div className="flex items-center gap-2 text-slate-600 mb-2">
                        <CalendarIcon className="w-5 h-5" />
                        <span className="text-sm font-semibold">Valid Period</span>
                      </div>
                      <p className="text-slate-900 font-medium">
                        {new Date(ticket.offerId.validFrom).toLocaleDateString()} - {new Date(ticket.offerId.validUntil).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {ticket.offerId.category && (
                    <div>
                      <div className="flex items-center gap-2 text-slate-600 mb-2">
                        <TagIcon className="w-5 h-5" />
                        <span className="text-sm font-semibold">Category</span>
                      </div>
                      <p className="text-slate-900 font-medium">{ticket.offerId.category}</p>
                    </div>
                  )}
                </>
              )}

              {/* Ticket Type */}
              {ticket.ticketTypeId?.name && (
                <div>
                  <div className="flex items-center gap-2 text-slate-600 mb-2">
                    <TicketIcon className="w-5 h-5" />
                    <span className="text-sm font-semibold">Ticket Type</span>
                  </div>
                  <p className="text-slate-900 font-medium">{ticket.ticketTypeId.name}</p>
                </div>
              )}

              {/* Attendee Info */}
              {user && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-slate-600 text-sm font-semibold mb-2">Attendee</p>
                  <p className="text-slate-900 font-medium">{user.name || user.email}</p>
                  {user.email && <p className="text-slate-600 text-sm">{user.email}</p>}
                </div>
              )}
            </div>

            {/* Right Column - QR Code */}
            <div className="flex flex-col items-center justify-center print:justify-start">
              <div className="bg-white p-6 rounded-2xl border-4 border-slate-300 shadow-lg flex items-center justify-center print:p-3 print:border-2 print:rounded-lg print:shadow-none">
                {isReady && qrData ? (
                  <div className="p-4 bg-white print:p-2">
                    <QRCode
                      value={qrData}
                      size={200}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      viewBox={`0 0 200 200`}
                      className="print:!w-[150px] print:!h-[150px]"
                    />
                  </div>
                ) : (
                  <div className="w-[200px] h-[200px] flex items-center justify-center bg-slate-100 rounded-lg print:w-[150px] print:h-[150px]">
                    <p className="text-xs text-slate-400 text-center">Loading QR Code...</p>
                  </div>
                )}
              </div>
              <p className="mt-4 text-xs text-slate-500 text-center max-w-xs print:mt-2 print:text-[10px]">
                Present this QR code at entry for verification
              </p>
              <div className="mt-4 p-3 bg-white rounded-lg border border-slate-200 print:mt-2 print:p-2">
                <p className="text-xs text-slate-500 text-center mb-1 print:text-[10px] print:mb-0">Ticket Status</p>
                <p className={`text-sm font-bold text-center print:text-xs ${
                  ticket.status === 'ACTIVE' ? 'text-green-600' : 
                  ticket.status === 'USED' ? 'text-gray-600' : 'text-red-600'
                }`}>
                  {ticket.status || 'UNKNOWN'}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200 print:mt-3 print:pt-3">
            <div className="flex items-center justify-between text-xs text-slate-500 print:text-[10px]">
              <div>
                <p className="font-semibold mb-1 print:mb-0 print:text-[10px]">Issued On</p>
                <p className="print:text-[10px]">{new Date(ticket.issuedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold mb-1 print:mb-0 print:text-[10px]">Ticket ID</p>
                <p className="font-mono print:text-[10px]">{ticket._id?.slice(-8).toUpperCase()}</p>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="mt-6 pt-6 border-t border-slate-200 print:mt-2 print:pt-2">
            <p className="text-xs text-slate-500 text-center leading-relaxed print:text-[9px] print:leading-tight">
              This ticket is non-transferable and valid only for the original purchaser. 
              Please keep this ticket safe and present the QR code for entry verification. 
              Any unauthorized duplication or use is prohibited.
            </p>
          </div>
        </div>
      </div>

      {/* Print Button (Not part of printable area) */}
      <div className="mt-6 flex justify-center gap-4 print:hidden">
        <button
          onClick={onPrint}
          className="px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl transition-colors shadow-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print Ticket
        </button>
      </div>
      </div>
    </>
  )
}

export default TicketTemplate

